import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Warehouse, PackageSearch, Boxes, PackageCheck, Truck, FileText, ArrowRight, Inbox, RefreshCw, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader, Modal } from "../lib/ui";
import { fmtRp } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import { usePrintDoc } from "../lib/usePrintDoc";
import OrderDetailModal from "../components/OrderDetailModal";

const COLUMNS = [
  { key: "to_pick", label: "To Pick", icon: PackageSearch, action: "PICKING", actionLabel: "Start Picking", tone: "text-amber-600 bg-amber-50" },
  { key: "picking", label: "Picking", icon: Boxes, action: "PACKING", actionLabel: "Finish → Packing", tone: "text-blue-600 bg-blue-50" },
  { key: "packing", label: "Packing", icon: PackageCheck, action: "READY", actionLabel: "Delivery Note & Ready", tone: "text-violet-600 bg-violet-50" },
  { key: "ready", label: "Ready for Delivery", icon: Truck, action: null, actionLabel: null, tone: "text-emerald-600 bg-emerald-50" },
];

export default function WarehouseWorkspace() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { printDoc, printing } = usePrintDoc();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [resolveFor, setResolveFor] = useState(null);
  const [resolution, setResolution] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewId, setViewId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/workspace/warehouse"); setData(data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitResolve = async () => {
    setSaving(true);
    try {
      await api.post(`/discrepancies/${resolveFor.id}/resolve`, { resolution: resolution.trim() });
      toast.success(t("Variance resolved"));
      setResolveFor(null); setResolution("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSaving(false); }
  };

  const advance = async (orderId, stage, withSj) => {
    setBusy(orderId);
    try {
      if (withSj) await printDoc(`/orders/${orderId}/surat-jalan`, { key: `ww-${orderId}`, label: t("Surat Jalan") });
      await api.post(`/orders/${orderId}/fulfillment`, { stage });
      toast.success(t("Updated"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(null); }
  };

  if (!data) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="warehouse-workspace">
      <PageHeader icon={Warehouse} title={t("Warehouse Workspace")} subtitle={t("Receive → Pick → Pack → Delivery Note → Ready")}>
        <button onClick={load} className="rounded-xl border border-line bg-white p-2 text-ink-soft transition hover:text-primary" data-testid="ww-refresh"><RefreshCw size={16} /></button>
      </PageHeader>

      {/* Receive supplier */}
      <Card className="mt-5 flex items-center gap-4 p-4" data-testid="ww-receive">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-light text-primary"><Inbox size={20} /></span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-base font-extrabold text-ink">{t("Receive Supplier Goods")}</p>
          <p className="text-xs text-ink-mute">{data.to_receive} {t("purchase orders awaiting receipt")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/procurement")} data-testid="ww-open-receive">{t("Open")} <ArrowRight size={15} /></Button>
      </Card>

      {/* Fulfilment pipeline */}
      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-4">
        {COLUMNS.map((col) => {
          const rows = data.stages[col.key] || [];
          const Icon = col.icon;
          return (
            <div key={col.key} className="rounded-2xl border border-line bg-app/50 p-3" data-testid={`ww-col-${col.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${col.tone}`}><Icon size={16} /></span>
                <h3 className="font-heading text-sm font-extrabold text-ink">{t(col.label)}</h3>
                <Badge tone="mute" className="ml-auto">{rows.length}</Badge>
              </div>
              <div className="space-y-2">
                {rows.length === 0 && <p className="py-4 text-center text-xs text-ink-mute">{t("Empty")}</p>}
                {rows.map((o) => (
                  <Card key={o.order_id} onClick={() => setViewId(o.order_id)} className="cursor-pointer p-3 transition hover:shadow-cardhover" data-testid={`ww-order-${o.order_no}`}>
                    <p className="font-bold text-ink">{o.branch_name}</p>
                    <p className="text-xs text-ink-mute">{o.item_count} {t("items")} · <span className="font-mono">Ref: {o.order_no}</span></p>
                    {col.action ? (
                      <Button size="sm" className="mt-2 w-full" loading={busy === o.order_id}
                        onClick={(e) => { e.stopPropagation(); advance(o.order_id, col.action, col.action === "READY"); }}
                        data-testid={`ww-advance-${o.order_no}`}>
                        {col.action === "READY" && <FileText size={14} />} {t(col.actionLabel)}
                      </Button>
                    ) : (
                      <div className="mt-2 flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1" loading={printing === `ww-${o.order_id}`} onClick={(e) => { e.stopPropagation(); printDoc(`/orders/${o.order_id}/surat-jalan`, { key: `ww-${o.order_id}`, label: t("Surat Jalan") }); }} data-testid={`ww-sj-${o.order_no}`}><FileText size={14} /> {t("Print Delivery Note")}</Button>
                        <Button size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); navigate("/delivery"); }} data-testid={`ww-deliver-${o.order_no}`}>{t("Deliver")}</Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-center gap-2 rounded-2xl border border-dashed border-line bg-app py-3 text-sm text-ink-soft" data-testid="ww-handoff">
        {t("Next")}: <b className="text-ink">{t("Delivery")}</b> <ArrowRight size={15} className="text-primary" />
      </div>

      {/* Receiving variances to investigate */}
      <Card className="mt-5 overflow-hidden p-0" data-testid="ww-variances">
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <AlertTriangle size={18} className="text-warn-text" />
          <h3 className="font-heading text-base font-extrabold">{t("Receiving Variances to Investigate")}</h3>
          <Badge tone={data.variances?.length ? "danger" : "mute"} className="ml-auto">{data.variances?.length || 0}</Badge>
        </div>
        {(!data.variances || data.variances.length === 0) ? (
          <p className="px-5 py-8 text-center text-sm text-ink-mute">{t("No open variances — everything reconciled")}</p>
        ) : (
          <div className="divide-y divide-line">
            {data.variances.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`ww-variance-${v.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink">{v.product_name || v.product_id} · <span className="text-danger-text">{t(v.issue_type)}</span></p>
                  <p className="text-xs text-ink-mute">
                    {v.order_no} · {v.branch_name} · {t("expected")} {v.expected_quantity} / {t("received")} {v.received_quantity}
                    {v.delivery_note_no ? ` · SJ ${v.delivery_note_no}` : ""}
                  </p>
                </div>
                <span className="font-heading text-sm font-extrabold text-danger-text">-{fmtRp(v.value_impact)}</span>
                <Button size="sm" variant="outline" onClick={() => { setResolveFor(v); setResolution(""); }} data-testid={`ww-resolve-${v.id}`}>
                  <ShieldCheck size={14} /> {t("Resolve")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={!!resolveFor} onClose={() => setResolveFor(null)} title={`${t("Resolve Variance")} · ${resolveFor?.order_no || ""}`}
        footer={<>
          <Button variant="ghost" onClick={() => setResolveFor(null)}>{t("Cancel")}</Button>
          <Button onClick={submitResolve} loading={saving} data-testid="ww-resolve-confirm">{t("Resolve")}</Button>
        </>}>
        <p className="mb-3 text-sm text-ink-soft">{t("Record the investigation outcome. The Owner will be notified.")}</p>
        <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3} data-testid="ww-resolution"
          className="w-full rounded-xl border border-line p-3 text-sm focus:border-primary focus:outline-none"
          placeholder={t("e.g. Supplier shortfall confirmed; credit note requested")} />
      </Modal>

      <OrderDetailModal orderId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
